import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  CloseButton,
  Code,
  Divider,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronRight, IconDownload, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useBatchAudit, useExportAudit } from "../../api/hooks";
import type { AuditEntry, BatchDetail } from "../../api/types";
import { formatPacificDisplay } from "../../lib/datetime";
import { downloadBlob } from "../../lib/download";
import { formatApiError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";

const ACTION_COLOR: Record<string, string> = {
  "issue.created": "blue",
  "issue.acknowledged": "cyan",
  "batch.transition": "indigo",
  "batch.submitted": "teal",
  "batch.created": "gray",
  "batch.restated": "orange",
  "correction.approved": "cyan",
  "config.activated": "violet",
  "report.generated": "teal",
};

// actor_email / actor_display_name come from a LEFT JOIN to compliance.users and
// are null for system-generated rows (no user_id) AND for deleted users. Prefer
// the display name, fall back to email, then to the user_id, and only call it
// "system" when there is genuinely no actor. A present user_id means a user
// initiated this event — falling straight through to "system" would misattribute
// a deleted user's action as system-generated, which is regulator-critical.
export function actorLabel(entry: AuditEntry): string {
  return (
    entry.actor_display_name ??
    entry.actor_email ??
    (entry.user_id ? `user ${entry.user_id}` : "system")
  );
}

export function AuditTab({ batch }: { batch: BatchDetail }) {
  const auditQ = useBatchAudit(batch.batch_id);
  const entries = useMemo(() => auditQ.data?.items ?? [], [auditQ.data]);
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const exportAudit = useExportAudit();
  const queryClient = useQueryClient();

  const onExport = async () => {
    try {
      const result = await exportAudit.mutateAsync({ batchId: batch.batch_id });
      downloadBlob(result.blob, result.filename ?? `audit_${batch.batch_id}.csv`);
    } catch (err) {
      notifications.show({
        title: "Export failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Title order={4}>Audit timeline</Title>
          <Group gap="xs">
            <Button
              data-tour="audit-export"
              size="xs"
              variant="default"
              leftSection={<IconDownload size={14} />}
              onClick={onExport}
              loading={exportAudit.isPending}
              disabled={entries.length === 0}
            >
              Export CSV
            </Button>
            <Tooltip label="Refresh">
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Refresh"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.batches.audit(batch.batch_id),
                  })
                }
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <Text size="sm" c="dimmed">
          Audit log scoped to this batch. Click a row to inspect the full record.
        </Text>
      </Stack>

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table
            data-tour="audit-table"
            verticalSpacing="sm"
            horizontalSpacing="md"
            fz="sm"
            highlightOnHover
            stickyHeader
            miw={920}
          >
            <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
              <Table.Tr>
                <Table.Th style={{ width: 190 }}>When (PT)</Table.Th>
                <Table.Th style={{ width: 170 }}>Actor</Table.Th>
                <Table.Th style={{ width: 170 }}>Action</Table.Th>
                <Table.Th>Entity</Table.Th>
                <Table.Th style={{ width: 32 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((e) => (
                <Table.Tr key={e.id} onClick={() => setSelected(e)} style={{ cursor: "pointer" }}>
                  <Table.Td ff="monospace" fz="xs">
                    {formatPacificDisplay(e.ts)}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {actorLabel(e)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={ACTION_COLOR[e.action] ?? "gray"} size="sm">
                      {e.action}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm" ff="monospace">
                        {e.entity_id ?? "—"}
                      </Text>
                      {e.reason ? (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {e.reason}
                        </Text>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <ThemeIcon variant="subtle" color="gray" size="sm">
                      <IconChevronRight size={14} />
                    </ThemeIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {auditQ.isError && entries.length > 0 ? (
          // Refetch failed but the last good page is still in cache — keep
          // showing it (labeled stale) instead of replacing the table with a
          // full error state, which would read as "no data".
          <Group justify="space-between" wrap="wrap" gap="sm" px="md" py="sm">
            <Text size="xs" c="red">
              Couldn't refresh — showing the last loaded results.
            </Text>
            <Button size="xs" variant="default" onClick={() => auditQ.refetch()}>
              Retry
            </Button>
          </Group>
        ) : auditQ.isError ? (
          <Stack align="center" py="xl" gap="sm">
            <Alert color="red" variant="light" title="Couldn't load the audit timeline" maw={520}>
              {formatApiError(auditQ.error)}
            </Alert>
            <Button size="xs" variant="default" onClick={() => auditQ.refetch()}>
              Retry
            </Button>
          </Stack>
        ) : auditQ.isLoading ? (
          <Stack align="center" py="xl" gap="xs">
            <Text c="dimmed" size="sm">
              Loading audit timeline…
            </Text>
          </Stack>
        ) : entries.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <Text c="dimmed" size="sm">
              No audit events recorded for this batch yet.
            </Text>
          </Stack>
        ) : null}
      </Paper>

      <AuditDrawer entry={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

function AuditDrawer({
  entry,
  onClose,
}: {
  entry: AuditEntry | null;
  onClose: () => void;
}) {
  return (
    <Drawer
      opened={entry !== null}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
      padding="lg"
    >
      {entry ? (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Badge variant="light" color={ACTION_COLOR[entry.action] ?? "gray"}>
                {entry.action}
              </Badge>
              <Title order={4} mt={4}>
                {entry.entity_id ?? "—"}
              </Title>
              <Text size="xs" c="dimmed" ff="monospace">
                {entry.id} · {entry.entity_type}
              </Text>
            </Stack>
            <CloseButton onClick={onClose} aria-label="Close details" />
          </Group>

          <Divider />

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Actor
            </Text>
            <Text size="sm">{actorLabel(entry)}</Text>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              When (PT)
            </Text>
            <Text size="sm" ff="monospace">
              {formatPacificDisplay(entry.ts)}
            </Text>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Reason
            </Text>
            <Text size="sm">{entry.reason ?? "—"}</Text>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Request ID
            </Text>
            <Text size="sm" ff="monospace">
              {entry.request_id ?? "—"}
            </Text>
          </Stack>

          {entry.before || entry.after ? (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
                Change (before → after)
              </Text>
              <Code block fz="xs">
                {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
              </Code>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}
